import { Component, ViewChild, OnInit, inject } from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltip } from "@angular/material/tooltip";
import { CommonModule } from '@angular/common';
import { AdminService } from '@adgate/modules/admin/services/admin-service';
import { AdminPanelModule } from '@adgate/modules/admin/types/admin-panel-module.interface';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { Agency } from '@adgate/common/types/agency.type';
import { filter, switchMap } from 'rxjs';
import { ObjectListPipe } from '@adgate/common/pipes/object-list-pipe';
import { AgencyEditDialogComponent } from '@adgate/modules/admin/dialogs/agency-edit-dialog-component/agency-edit-dialog-component';
import { QuickSearch } from '@adgate/common/interfaces/quick-search.interface';

@Component({
  selector: 'app-admin-panel-agencies-component',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatTooltip,
    ObjectListPipe
  ],
  templateUrl: './admin-panel-agencies-component.html',
  styleUrl: './admin-panel-agencies-component.scss',
})
export class AdminPanelAgenciesComponent implements OnInit, AdminPanelModule, QuickSearch
{
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private adminService: AdminService = inject(AdminService);
  private dialogService: DialogService = inject(DialogService);

  displayedColumns: string[] = ['name', 'office', 'externalId', 'actions'];
  dataSource: MatTableDataSource<Agency> = new MatTableDataSource<Agency>();

  constructor()
  {

  }

  ngOnInit(): void
  {
    this.onRefresh();
  }

  ngAfterViewInit()
  {
    this.dataSource.sortingDataAccessor = this.getTableDataAccessor();
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onRefresh(): void
  {
    this.dialogService.showProgress();
    this.adminService.getAgencies().subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.hideProgress();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  getQuickSearchPlaceholder(): string 
  {
    return 'Search Agencies';    
  }

  onQuickSearch(filter: string | null)
  {
    this.dataSource.filter = filter!;
  }

  onNewAgency(): void
  {
    this.onEditAgency(new Agency());
  }

  onEditAgency(agency: Agency): void
  {
    let successMessage: string;

    const dialogRef = this.dialogService.openAnyDialog(AgencyEditDialogComponent, {
      data: agency,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: Agency | null) => result !== null),
      switchMap((result: Agency) => {
        this.dialogService.showProgress();
        return this.adminService.updateAgency(result);
      }),
      switchMap((r:RESTApplicationResponse) => {
        successMessage = r.message;
        return this.adminService.getAgencies();
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.openInfoDialog(successMessage);
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onDeleteAgency(row: Agency): void
  {
    const dialogRef = this.dialogService.openConfirmDialog(
      `Delete agency "${row.description}"?`,
      {
        title: 'Delete Agency',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        severity: 'danger'
      }
    );

    dialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed == true ),
      switchMap(() => {
        this.dialogService.showProgress();
        return this.adminService.deleteAgency(row.id);
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.dialogService.openInfoDialog(r.message);
        this.onRefresh()
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  getTableDataAccessor(): any
  {
    return (item: any, property: any) => {
      switch(property) {
        case 'name': return item.description;
        case 'office': return (item.office != null ? item.office.description : '');
        case 'externalId': return item.externalId;
        default: return item[property];
      }
    };
  }
}
